import React, { useState, useEffect, useRef } from 'react';
import { 
  Camera, 
  Image, 
  Trash, 
  Plus,
  X,
  SpinnerGap,
  CheckCircle,
  CaretLeft,
  CaretRight,
  Folder,
  CloudArrowUp,
  Eye
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { jobPhotosAPI, portfolioAPI } from '../api/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

const PHOTO_TYPES = [
  { id: 'before', label: 'Before', color: 'bg-amber-500', description: 'Initial state before work begins' },
  { id: 'progress', label: 'Progress', color: 'bg-blue-500', description: 'Work in progress updates' },
  { id: 'after', label: 'After', color: 'bg-green-500', description: 'Completed work showcase' },
  { id: 'milestone', label: 'Milestone', color: 'bg-purple-500', description: 'Milestone completion proof' },
];

// Photo Upload Modal Component
const PhotoUploadModal = ({ 
  isOpen, 
  onClose, 
  jobId, 
  milestoneId = null, 
  defaultType = 'progress',
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    photo_type: milestoneId ? 'milestone' : defaultType,
    title: '',
    description: '',
    add_to_portfolio: false
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please select an image file', variant: 'destructive' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Image must be less than 10MB', variant: 'destructive' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!imagePreview) {
      toast({ title: 'No image', description: 'Please select an image to upload', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await jobPhotosAPI.addPhoto({
        job_id: jobId,
        image_data: imagePreview,
        photo_type: formData.photo_type,
        title: formData.title || undefined,
        description: formData.description || undefined,
        milestone_id: milestoneId || undefined,
        add_to_portfolio: formData.add_to_portfolio
      });

      if (response.data.success) {
        toast({ 
          title: 'Photo uploaded', 
          description: formData.add_to_portfolio 
            ? 'Photo added and saved to your portfolio' 
            : 'Photo documentation added successfully'
        });
        onSuccess?.(response.data.photo);
        onClose();
        setImagePreview(null);
        setFormData({ photo_type: defaultType, title: '', description: '', add_to_portfolio: false });
      }
    } catch (error) {
      toast({ 
        title: 'Upload failed', 
        description: error.response?.data?.detail || 'Failed to upload photo', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const userType = localStorage.getItem('userType');
  const isWorker = userType === 'worker';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
        <DialogHeader>
          <DialogTitle className="text-xl font-black text-neutral-900 dark:text-white flex items-center gap-2">
            <Camera size={24} weight="bold" className="text-[#2B4C6F]" />
            Add Photo Documentation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Image Upload Area */}
          <div 
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
              imagePreview 
                ? 'border-[#2B4C6F] bg-blue-50 dark:bg-blue-900/20' 
                : 'border-neutral-300 dark:border-neutral-600 hover:border-[#2B4C6F] hover:bg-neutral-50 dark:hover:bg-neutral-700'
            }`}
          >
            {imagePreview ? (
              <div className="relative">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="max-h-48 mx-auto rounded-lg"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setImagePreview(null);
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            ) : (
              <>
                <CloudArrowUp size={48} weight="bold" className="mx-auto text-neutral-400 mb-2" />
                <p className="font-bold text-neutral-600 dark:text-neutral-300">Click to upload image</p>
                <p className="text-sm text-neutral-400">PNG, JPG up to 10MB</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Photo Type Selection */}
          <div>
            <Label className="font-bold text-neutral-900 dark:text-white mb-2 block">Photo Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {PHOTO_TYPES.filter(t => !milestoneId || t.id === 'milestone').map((type) => (
                <button
                  key={type.id}
                  onClick={() => setFormData({ ...formData, photo_type: type.id })}
                  disabled={milestoneId && type.id !== 'milestone'}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    formData.photo_type === type.id
                      ? 'border-[#2B4C6F] bg-blue-50 dark:bg-blue-900/30'
                      : 'border-neutral-200 dark:border-neutral-600 hover:border-[#2B4C6F]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${type.color}`} />
                    <span className="font-bold text-neutral-900 dark:text-white">{type.label}</span>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{type.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="photo-title" className="font-bold text-neutral-900 dark:text-white">Title (Optional)</Label>
            <Input
              id="photo-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Kitchen demolition complete"
              className="mt-1 border-2 border-neutral-200 dark:border-neutral-600"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="photo-desc" className="font-bold text-neutral-900 dark:text-white">Description (Optional)</Label>
            <Textarea
              id="photo-desc"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add notes about this photo..."
              className="mt-1 border-2 border-neutral-200 dark:border-neutral-600 min-h-[80px]"
            />
          </div>

          {/* Add to Portfolio Option (Workers only) */}
          {isWorker && (
            <label className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-700 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={formData.add_to_portfolio}
                onChange={(e) => setFormData({ ...formData, add_to_portfolio: e.target.checked })}
                className="w-5 h-5 rounded border-2 border-neutral-300"
              />
              <div>
                <span className="font-bold text-neutral-900 dark:text-white">Add to my portfolio</span>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Showcase this work on your profile</p>
              </div>
            </label>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !imagePreview}
            className="bg-[#2B4C6F] hover:bg-[#203A55] border-2 border-black shadow-[2px_2px_0px_#000]"
          >
            {loading ? (
              <><SpinnerGap size={20} className="animate-spin mr-2" /> Uploading...</>
            ) : (
              <><Plus size={20} weight="bold" className="mr-2" /> Add Photo</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Photo Gallery Component
const JobPhotoGallery = ({ 
  jobId, 
  milestoneId = null,
  canUpload = false,
  showGrouped = true,
  compact = false
}) => {
  const [photos, setPhotos] = useState([]);
  const [grouped, setGrouped] = useState({ before: [], progress: [], after: [], milestone: [] });
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      let response;
      if (milestoneId) {
        response = await jobPhotosAPI.getMilestonePhotos(milestoneId);
      } else {
        response = await jobPhotosAPI.getJobPhotos(jobId);
      }
      
      if (response.data.success) {
        setPhotos(response.data.photos);
        setGrouped(response.data.grouped || { before: [], progress: [], after: [], milestone: [] });
      }
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId || milestoneId) {
      fetchPhotos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [jobId, milestoneId]);

  const handleDelete = async (photoId) => {
    if (!confirm('Delete this photo?')) return;
    
    try {
      await jobPhotosAPI.deletePhoto(photoId);
      toast({ title: 'Photo deleted' });
      fetchPhotos();
    } catch (error) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const handleAddToPortfolio = async (photoId) => {
    try {
      const response = await jobPhotosAPI.addToPortfolio(photoId);
      if (response.data.success) {
        toast({ title: 'Added to portfolio', description: 'Photo saved to your profile' });
      } else {
        toast({ title: response.data.message });
      }
    } catch (error) {
      toast({ title: 'Failed to add to portfolio', variant: 'destructive' });
    }
  };

  const openLightbox = (photo, index) => {
    setLightboxPhoto(photo);
    setLightboxIndex(index);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setLightboxIndex(newIndex);
      setLightboxPhoto(photos[newIndex]);
    }
  };

  const userType = localStorage.getItem('userType');
  const userId = localStorage.getItem('userId');
  const isWorker = userType === 'worker';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <SpinnerGap size={32} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  const renderPhotoCard = (photo, index) => {
    const typeInfo = PHOTO_TYPES.find(t => t.id === photo.photo_type) || PHOTO_TYPES[1];
    const canDelete = photo.user_id === userId;
    
    return (
      <div 
        key={photo._id}
        className="group relative bg-white dark:bg-neutral-800 border-2 border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden hover:shadow-lg transition-all"
      >
        {/* Image */}
        <div 
          className="aspect-square cursor-pointer overflow-hidden"
          onClick={() => openLightbox(photo, index)}
        >
          <img 
            src={photo.image_url} 
            alt={photo.title || 'Job photo'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        </div>

        {/* Type Badge */}
        <div className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-bold text-white ${typeInfo.color}`}>
          {typeInfo.label}
        </div>

        {/* Actions Overlay */}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {isWorker && (
            <button
              onClick={(e) => { e.stopPropagation(); handleAddToPortfolio(photo._id); }}
              className="p-2 bg-white/90 dark:bg-neutral-800/90 rounded-lg hover:bg-white shadow-md"
              title="Add to portfolio"
            >
              <Folder size={16} weight="bold" className="text-[#2B4C6F]" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(photo._id); }}
              className="p-2 bg-white/90 dark:bg-neutral-800/90 rounded-lg hover:bg-red-50 shadow-md"
              title="Delete"
            >
              <Trash size={16} weight="bold" className="text-red-500" />
            </button>
          )}
        </div>

        {/* Info */}
        {!compact && (
          <div className="p-3">
            <p className="font-bold text-neutral-900 dark:text-white text-sm truncate">
              {photo.title || `${typeInfo.label} Photo`}
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              by {photo.user_name} • {new Date(photo.created_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderGroupedView = () => (
    <div className="space-y-6">
      {PHOTO_TYPES.map((type) => {
        const typePhotos = grouped[type.id] || [];
        if (typePhotos.length === 0) return null;
        
        return (
          <div key={type.id}>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-3 h-3 rounded-full ${type.color}`} />
              <h4 className="font-bold text-neutral-900 dark:text-white">{type.label} Photos</h4>
              <span className="text-sm text-neutral-500">({typePhotos.length})</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {typePhotos.map((photo, idx) => renderPhotoCard(photo, photos.indexOf(photo)))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Image size={24} weight="bold" className="text-[#2B4C6F]" />
          <h3 className="text-lg font-black text-neutral-900 dark:text-white">
            Photo Documentation
          </h3>
          <span className="text-sm text-neutral-500">({photos.length})</span>
        </div>
        {canUpload && (
          <Button
            onClick={() => setShowUpload(true)}
            className="bg-[#2B4C6F] hover:bg-[#203A55] border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none"
            data-testid="add-photo-btn"
          >
            <Camera size={20} weight="bold" className="mr-2" />
            Add Photo
          </Button>
        )}
      </div>

      {/* Photos Grid */}
      {photos.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 dark:bg-neutral-800 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-700">
          <Image size={48} weight="bold" className="mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400 font-medium">No photos yet</p>
          {canUpload && (
            <Button 
              onClick={() => setShowUpload(true)}
              variant="outline"
              className="mt-3"
            >
              <Plus size={16} weight="bold" className="mr-1" /> Add First Photo
            </Button>
          )}
        </div>
      ) : showGrouped ? (
        renderGroupedView()
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo, idx) => renderPhotoCard(photo, idx))}
        </div>
      )}

      {/* Upload Modal */}
      <PhotoUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        jobId={jobId}
        milestoneId={milestoneId}
        onSuccess={(photo) => {
          setPhotos([photo, ...photos]);
          const newGrouped = { ...grouped };
          newGrouped[photo.photo_type] = [photo, ...(newGrouped[photo.photo_type] || [])];
          setGrouped(newGrouped);
        }}
      />

      {/* Lightbox */}
      {lightboxPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white"
          >
            <X size={32} weight="bold" />
          </button>
          
          {lightboxIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              className="absolute left-4 p-2 text-white/70 hover:text-white"
            >
              <CaretLeft size={48} weight="bold" />
            </button>
          )}
          
          {lightboxIndex < photos.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              className="absolute right-4 p-2 text-white/70 hover:text-white"
            >
              <CaretRight size={48} weight="bold" />
            </button>
          )}

          <div className="max-w-4xl max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxPhoto.image_url} 
              alt={lightboxPhoto.title}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center text-white">
              <p className="font-bold text-lg">{lightboxPhoto.title || 'Photo'}</p>
              {lightboxPhoto.description && (
                <p className="text-white/70 mt-1">{lightboxPhoto.description}</p>
              )}
              <p className="text-white/50 text-sm mt-2">
                {PHOTO_TYPES.find(t => t.id === lightboxPhoto.photo_type)?.label} • 
                by {lightboxPhoto.user_name} • 
                {new Date(lightboxPhoto.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Compact version for Escrow milestones
export const MilestonePhotoUpload = ({ milestoneId, jobId, milestoneName, onPhotoAdded }) => {
  const [showUpload, setShowUpload] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (milestoneId) {
      jobPhotosAPI.getMilestonePhotos(milestoneId)
        .then(res => {
          if (res.data.success) setPhotos(res.data.photos);
        })
        .finally(() => setLoading(false));
    }
  }, [milestoneId]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-neutral-600 dark:text-neutral-400">
          Proof Photos ({photos.length})
        </span>
        <button
          onClick={() => setShowUpload(true)}
          className="text-sm text-[#2B4C6F] hover:underline font-bold flex items-center gap-1"
        >
          <Plus size={14} weight="bold" /> Add Proof
        </button>
      </div>
      
      {photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {photos.slice(0, 4).map(photo => (
            <img 
              key={photo._id}
              src={photo.image_url}
              alt="Milestone proof"
              className="w-16 h-16 object-cover rounded-lg border-2 border-neutral-200 dark:border-neutral-600"
            />
          ))}
          {photos.length > 4 && (
            <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-700 rounded-lg border-2 border-neutral-200 dark:border-neutral-600 flex items-center justify-center">
              <span className="text-sm font-bold text-neutral-500">+{photos.length - 4}</span>
            </div>
          )}
        </div>
      )}

      <PhotoUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        jobId={jobId}
        milestoneId={milestoneId}
        defaultType="milestone"
        onSuccess={(photo) => {
          setPhotos([photo, ...photos]);
          onPhotoAdded?.(photo);
        }}
      />
    </div>
  );
};

export { PhotoUploadModal };
export default JobPhotoGallery;
